import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isArtifactViewable,
  resourceArtifactData,
} from '@cotera/client/v0/chat/artifacts';
import { useArtifactPanel } from '@cotera/client/v0/chat/artifact-panel-context';
import { useResourceArtifactPanel } from '../../chat/artifacts/resource-artifact-panel-context';
import { useWhoami } from '../../../stores/org';
import {
  ArtifactPanelResourceNavigator,
  PageResourceNavigator,
  type ResourceNavigator,
  type ResourceTarget,
} from './resource-navigator';

/** The app-level navigator: always routes to the resource's full page. */
export const usePageResourceNavigator = (): PageResourceNavigator => {
  const navigate = useNavigate();
  const orgSlug = useWhoami((s) => s.org.slug);
  return useMemo(
    () => new PageResourceNavigator(navigate, orgSlug),
    [navigate, orgSlug]
  );
};

const buildPayload = (target: ResourceTarget): Record<string, unknown> => ({
  ...resourceArtifactData(target.resourceType, target.resourceId, target.name),
  ...target.payload,
});

/**
 * The best surface for the caller's position in the tree, in priority order:
 *
 * 1. **Standalone editor page** (inside a `ResourceArtifactPanelProvider`) — show
 *    the resource in the page's transient side panel.
 * 2. **Inside a chat artifact panel** — open it as a *sibling artifact tab* so the
 *    conversation isn't disrupted.
 * 3. **Otherwise** — navigate to the resource's full page.
 *
 * Both panel contexts are React context rather than models, so this resolves
 * correctly wherever it is mounted, model scope or not.
 */
export const useResourceNavigator = (): ResourceNavigator => {
  const pageNavigator = usePageResourceNavigator();
  const resourceArtifactPanel = useResourceArtifactPanel();
  const chatArtifactPanel = useArtifactPanel();

  return useMemo(() => {
    if (resourceArtifactPanel !== null) {
      return new ArtifactPanelResourceNavigator({
        openArtifact: ({ resourceType, resourceId, name, payload }) =>
          resourceArtifactPanel.openResourceArtifact(resourceType, resourceId, {
            ...(name !== undefined && { name }),
            payload,
          }),
        canOpenAsArtifact: isArtifactViewable,
        fallback: pageNavigator,
        buildPayload,
      });
    }
    if (chatArtifactPanel !== null) {
      return new ArtifactPanelResourceNavigator({
        openArtifact: ({ resourceType, resourceId, payload }) =>
          chatArtifactPanel.openArtifact({
            type: resourceType,
            artifactId: resourceId,
            payload,
          }),
        canOpenAsArtifact: isArtifactViewable,
        fallback: pageNavigator,
        buildPayload,
      });
    }
    return pageNavigator;
  }, [resourceArtifactPanel, chatArtifactPanel, pageNavigator]);
};
